<script setup>
import { ref, computed, nextTick, getCurrentInstance, onMounted } from 'vue'
import { tqueue } from '@/common/toast'
import Spinner from '@/components/Flowbite/Spinner.vue'

const props = defineProps({
  type: {
    type: String,
    default: "success" //none, info, success, warning, error, loading
  },
  text: { //message
    type: String,
    default: ""
  },
  canClose: {
    default: false
  },
  timeout: {
    type: Number,
    default: 3000
  },
  _id: {
    type: String,
    default: null
  }
});

let timer = null;

function close() {
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }

  let app = tqueue[props._id];
  if (app) {
    app.unmount();
    delete tqueue[props._id];
  }

  let div = document.getElementById("mount-toast-" + props._id);
  let container = document.getElementById("mount-toast");
  if (container && div) {
    container.removeChild(div);
  }
}

onMounted(() => {
  if (props.timeout > 0) {
    timer = setTimeout(() => close(), props.timeout);
  }
});
</script>

<template>
  <div class="grid place-items-center">
    <div :class="'status-' + type" class="flex items-center w-full max-w-xs p-4 mb-4 rounded-lg shadow text-gray-500 dark:text-white dark:text-gray-400" role="alert">

      <!-- Info icon -->
      <div class="inline-flex items-center justify-center flex-shrink-0 w-8 h-8 text-blue-500 rounded-lg dark:text-blue-200" v-if="type == 'info'">
        <i class="fa fa-info-circle"></i>
      </div>

      <!-- Success -->
      <div class="inline-flex items-center justify-center flex-shrink-0 w-8 h-8 text-green-500 rounded-lg dark:text-green-200" v-if="type == 'success'">
        <svg class="w-5 h-5" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 20 20">
          <path d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5Zm3.707 8.207-4 4a1 1 0 0 1-1.414 0l-2-2a1 1 0 0 1 1.414-1.414L9 10.586l3.293-3.293a1 1 0 0 1 1.414 1.414Z"/>
        </svg>
        <span class="sr-only">Check icon</span>
      </div>

      <!-- Warning -->
      <div class="inline-flex items-center justify-center flex-shrink-0 w-8 h-8 text-yellow-300 rounded-lg dark:text-yellow-900" v-if="type == 'warning'">
        <i class="fa fa-triangle-exclamation"></i>
      </div>

      <!-- Error -->
      <div class="inline-flex items-center justify-center flex-shrink-0 w-8 h-8 text-red-500 bg-transparent rounded-lg dark:text-red-200" v-if="type == 'danger'">
        <svg class="w-5 h-5" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 20 20">
          <path d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5Zm3.707 11.793a1 1 0 1 1-1.414 1.414L10 11.414l-2.293 2.293a1 1 0 0 1-1.414-1.414L8.586 10 6.293 7.707a1 1 0 0 1 1.414-1.414L10 8.586l2.293-2.293a1 1 0 0 1 1.414 1.414L11.414 10l2.293 2.293Z"/>
        </svg>
        <span class="sr-only">Error icon</span>
      </div>

      <!-- Loading -->
      <div class="inline-flex items-center justify-center flex-shrink-0 w-8 h-8 text-green-500  rounded-lg dark:text-green-200" v-if="type == 'loading'">
        <Spinner size="6" style="display: inline-block;" />
      </div>

      <!-- Message -->
      <div class="ml-3 text-sm font-normal">{{ text }}</div>

      <!-- Close button -->
      <button type="button" @click="close" class="toast-close-button ml-auto -mx-1.5 -my-1.5 bg-transparent text-gray-400 hover:text-gray-900 rounded-lg focus:ring-2 focus:ring-gray-300 p-1.5 hover:bg-gray-100 inline-flex items-center justify-center h-8 w-8 dark:text-white dark:hover:text-white dark:hover:bg-gray-700" data-dismiss-target="#toast-success" aria-label="Close" v-if="canClose">
        <span class="sr-only">Close</span>
        <svg class="w-3 h-3" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 14 14">
          <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"/>
        </svg>
      </button>

    </div>
  </div>
</template>

<style scoped>
.status-loading {
  @apply bg-white dark:bg-black;
}

.status-info {
  @apply bg-blue-100 dark:bg-blue-800;
}

.status-success {
  @apply bg-green-100 dark:bg-emerald-600;
}

.status-warning {
  @apply bg-yellow-100 dark:bg-yellow-400 dark:text-black;
}

.status-warning .toast-close-button {
  @apply dark:text-black dark:hover:text-black;
}

.status-danger {
  @apply bg-red-100 dark:bg-red-700;
}
</style>
